interface Props {
  name?: string
  target?: string
  network?: NetworkRecord
  secret?: string
  /**
   * No key: the signed-in enrollment (app#379, router `register_node` `enrollment: "signed_in"`).
   * Only `install` and `windows` have a keyless form; the other blocks need a key.
   */
  keyless?: boolean
  block: CodeBlockType
}

interface CodeBlockResult {
  code: string
  language: 'bash' | 'yaml'
  hidden: boolean
}

const image = 'nobgp/nobgp'

/** The blocks that have a keyless (signed-in) form. The others need the network's key. */
export const KEYLESS_BLOCKS: readonly CodeBlockType[] = ['install', 'windows']

// The end of each Windows command: download install.ps1 and run it.
// It says `Invoke-Expression`, never the alias `iex`. Microsoft Defender refuses to start
// `powershell -NoProfile -Command "... irm <url>.ps1 | iex"` ("Access is denied", threat
// Trojan:Win32/Commando.A!ml), with or without a key. The same line with `Invoke-Expression`
// started in every test (nobgp-agent#1459, measured 2026-10-01 on Windows 11). The verdict is
// from a model on the command line, so measure a new wording before a change. Must stay
// byte-identical to `windowsDownloadAndRun` in the router's `src/tools/registration.go`.
const WINDOWS_DOWNLOAD_AND_RUN = 'irm https://downloads.nobgp.com/agent/install.ps1 | Invoke-Expression'

export default function useCodeBlock({ block, name = '', secret = '-', keyless = false }: Props): CodeBlockResult {
  const hasName = !!name.trim()

  // ⚠⚠ THE SIGNED-IN ENROLLMENT, THE ADD NODE DEFAULT FOR EVERY MEMBER (app#379, Benoit 2026-10-21,
  // docs/PERMISSIONS.md § 5 Node: "a signed-in member through `register_node` (who then owns it)").
  // The installer with no NOBGP_KEY runs `nobgp register`, which prints a sign-in link and a QR
  // code; the member signs in there, chooses the network, names the node and owns it. These two
  // strings must stay byte-identical to `shellSignedInCommand` / `windowsSignedInCommand` in the
  // router's `src/tools/registration.go`, as the keyed ones match `shellInstallCommand` /
  // `windowsInstallCommand`.
  if (keyless && block === 'install') {
    return {
      code: `curl -fsSL https://downloads.nobgp.com/agent/install.sh |${hasName ? ` NOBGP_NAME=${name}` : ''} sh`,
      language: 'bash',
      hidden: false,
    }
  }
  if (keyless && block === 'windows') {
    return {
      code: `powershell -NoProfile -Command "${hasName ? `Set-Item env:NOBGP_NAME '${name}'; ` : ''}${WINDOWS_DOWNLOAD_AND_RUN}"`,
      language: 'bash',
      hidden: false,
    }
  }

  let code = ''
  let language: 'bash' | 'yaml' = 'bash'
  // ⚠ Every keyed block carries the network's key, so every one is masked in a session recording
  // (`ph-mask`), not only the env block (app#379: the key is read through an audited tool).
  const hidden = true

  switch (block) {
    case 'container':
      code = `docker run${hasName ? ` --name '${name}'` : ''} \\
  --detach --restart unless-stopped \\
  --pull always \\
  --env NOBGP_KEY='${secret}' \\${hasName ? `\n  --env NOBGP_NAME='${name}' \\` : ''}
  --security-opt apparmor=unconfined \\
  --cap-add NET_ADMIN \\
  --cap-add SYS_ADMIN \\
  --device=/dev/net/tun \\
  --device=/dev/fuse \\
  ${image}`
      break

    case 'compose':
      language = 'yaml'
      code = `services:
  node:
    image: ${image}
    pull_policy: always
    restart: unless-stopped
${hasName ? `    container_name: ${name}\n    hostname: ${name}\n` : ''}    cap_add:
      - NET_ADMIN
      - SYS_ADMIN
    devices:
      - /dev/net/tun
      - /dev/fuse
    environment:
      NOBGP_KEY: '${secret}'${hasName ? `\n      NOBGP_NAME: '${name}'` : ''}`
      break

    case 'install':
      code = `curl -fsSL https://downloads.nobgp.com/agent/install.sh | NOBGP_KEY=${secret}${hasName ? ` NOBGP_NAME=${name}` : ''} sh`
      break

    // One line for every Windows shell — cmd.exe, PowerShell 5.1 and PowerShell 7.
    // `Set-Item env:` rather than `$env:X=` is the whole trick: with no `$` in the
    // string, the *outer* PowerShell finds nothing to expand in the double-quoted
    // -Command argument and passes it through untouched (`$env:NOBGP_KEY='…'` would
    // expand to empty before the child ever saw it). cmd treats `|`, `;` and `'`
    // inside a double-quoted argument as literals, so the same bytes work there.
    // The old cmd-only variant was worse than a wrong-shell error: pasted into
    // PowerShell 7 it SUCCEEDS silently and installs an unregistered node — `&&` is
    // valid there and `set` is an alias for Set-Variable, so the key never reaches
    // the environment. install.ps1 self-elevates, so no UAC handling belongs here.
    case 'windows':
      code = `powershell -NoProfile -Command "Set-Item env:NOBGP_KEY '${secret}'${hasName ? `; Set-Item env:NOBGP_NAME '${name}'` : ''}; ${WINDOWS_DOWNLOAD_AND_RUN}"`
      break

    case 'env':
      code = `NOBGP_KEY=${secret}`
      break
  }

  return { code, language, hidden }
}
